import React, { useState, useEffect } from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { Field, reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import FormattedMessage from '../../FormattedMessage/FormattedMessage'
import { renderField } from '../../../utilities/reduxForm.util'
import { required, password, maxLength, minLength } from '../../../utilities/validators'
import { currentUser } from '../../../redux/reducers/user.reducers'
import Loader from '../../Loader/Loader'

const [minLength6, maxLength20] = [minLength(6), maxLength(20)]

const ResetPasswordForm = props => {
  const {
    error,
    submitting,
    handleSubmit,
    submitSucceeded,
    reset,
    user,
    validSession,
    onModalClose,
  } = props
  const [modal, setModal] = useState(false)
  const modalId = 'password-reset-complete-modal'
  const modalTitleId = 'password-reset-complete-title'

  useEffect(() => {
    if (submitSucceeded && !modal) {
      toggleModal()
      reset()
    }
  }, [submitSucceeded, reset, modal])

  const toggleModal = () => {
    const wasOpen = modal
    setModal(!modal)

    if (wasOpen && onModalClose) {
      onModalClose(validSession && user)
    }
  }

  return (
    <div className="reset-password-form-container">
      <form onSubmit={handleSubmit} method="post" aria-label="Reset password form">
        {/* Hidden username field for password managers */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
          tabIndex="-1"
          aria-hidden="true"
        />
        <div className="form-group">
          <Field
            component={renderField}
            validate={[required, minLength6, maxLength20, password]}
            inputType="input"
            type="password"
            password="true"
            name="password"
            id="new-password"
            label="password"
            aria-required="true"
            aria-describedby="password-requirements"
          />
          <div id="password-requirements" className="password-requirements sr-only">
            <FormattedMessage defaultMessage="Password must be between 6-20 characters and meet complexity requirements" />
          </div>
        </div>

        {error && (
          <div className="form-validation-error-message mb-2" role="alert" aria-live="assertive">
            <FormattedMessage id={error} />
          </div>
        )}

        {submitting ? (
          <div aria-live="polite">
            <Loader />
          </div>
        ) : (
          <Button type="submit" outline color="dark" block aria-label="Set new password">
            <FormattedMessage id="page.myAccountPage.button.newPassword" />
          </Button>
        )}
      </form>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        id={modalId}
        aria-labelledby={modalTitleId}
        role="dialog"
      >
        <ModalHeader toggle={toggleModal}>
          <span className="sr-only">
            <FormattedMessage id="global.label.close" defaultMessage="Close" />
          </span>
        </ModalHeader>
        <ModalBody>
          <h2 id={modalTitleId} className="text-center">
            <FormattedMessage id="success.passwordReset.completeTitle" />
          </h2>
          <p>
            <FormattedMessage id="success.passwordReset.completeDescription" />
          </p>
        </ModalBody>
        <ModalFooter>
          <div className="w-100">
            <div className="row">
              <div className="col">
                <Button
                  block
                  outline
                  color="light"
                  onClick={toggleModal}
                  aria-label="Close this message"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  )
}

ResetPasswordForm.propTypes = {
  error: PropTypes.string,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  submitSucceeded: PropTypes.bool,
  reset: PropTypes.func.isRequired,
  user: PropTypes.object,
  validSession: PropTypes.bool,
  onModalClose: PropTypes.func,
}

const mapStateToProps = state => ({
  user: currentUser(state),
  validSession: state.userReducers.validSession,
})

const ResetPasswordFormConnected = connect(mapStateToProps)(ResetPasswordForm)

export default reduxForm({
  form: 'resetPasswordForm',
  validate: () => ({}),
})(ResetPasswordFormConnected)
